import React, { useEffect, Suspense, lazy } from "react";
import { createBrowserRouter, RouterProvider, Route, Routes, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";

import { useUserItems } from "./hooks/useUserItems";
import { useDailyTaskReset } from "./hooks/useDailyTaskReset";
import { useUserProfile } from "./hooks/useUserProfile";

import AppLayout from "./Components/AppLayout";
import ErrorBoundary from "./Components/ErrorBoundary";
import TvShell from "./Components/TvShell";
import StandaloneLayout from "./Components/StandaloneLayout";
import CheckoutGuard from "./Guards/CheckoutGuard";
import ComingSoonGuard from "./Guards/ComingSoonGuard";
import FeatureGuard from "./Guards/FeatureGuard";
import OnboardingGuard from "./Guards/OnboardingGuard";
import { FeaturesProvider } from "./contexts/FeaturesContext";
import { FEATURES } from "./utils/features";


// -----------------------------------------------------------------------------
// Lazy-loaded route components (code-splitting)
// -----------------------------------------------------------------------------

const Landing = lazy(() => import("./Components/Landing"));
const Dashboard = lazy(() => import("./Components/Dashboard"));
const GroceryList = lazy(() => import("./Components/GroceryList"));
const MealPlanForm = lazy(() => import("./Components/MealPlanForm"));
const MealPlansLegacyRedirect = lazy(() =>
  import("./Components/MealsWorkflowRoutes").then((m) => ({ default: m.MealPlansLegacyRedirect }))
);
const AuthForm = lazy(() => import("./Components/Auth/AuthForm"));
const Profile = lazy(() => import("./Components/Profile"));
const Subscription = lazy(() => import("./Components/Subscription"));
const CheckoutPage = lazy(() => import("./Components/Checkout"));
const Docs = lazy(() => import("./Components/Docs"));
const House = lazy(() => import("./Components/House"));
const Fitness = lazy(() => import("./Components/Fitness"));
const ListsHub = lazy(() => import("./Components/ListsHub"));
const Lists = lazy(() => import("./Components/Lists"));
const VerticalStepperForm = lazy(() => import("./Components/Step"));
const Schedule = lazy(() => import("./Components/Schedule"));
const Cs2 = lazy(() => import("./Components/Cs2"));

const Selling = lazy(() => import("./Components/Selling"));
const EbayCallback = lazy(() => import("./Components/EbayCallback"));
const Search = lazy(() => import("./Components/Search"));
const CustomCardPage = lazy(() => import("./Components/CustomCardPage"));
const Agenda = lazy(() => import("./Components/Agenda"));
const SharedList = lazy(() => import("./Components/SharedList"));
const InviteListJoin = lazy(() => import("./Components/InviteListJoin"));
const Help = lazy(() => import("./Components/Help"));
const Privacy = lazy(() => import("./Components/Privacy"));
const Terms = lazy(() => import("./Components/Terms"));
const DeleteAccount = lazy(() => import("./Components/DeleteAccount"));
const TvMode = lazy(() => import("./Components/TvMode"));
const TvCookMode = lazy(() => import("./Components/TvCookMode"));
const TvMealPlan = lazy(() => import("./Components/TvMealPlan"));
const TvLists = lazy(() => import("./Components/TvLists"));
const TvBasketball = lazy(() => import("./Components/TvBasketball"));
const TvYoga = lazy(() => import("./Components/TvYoga"));
const TvStretch = lazy(() => import("./Components/TvStretch"));
const TvMeditation = lazy(() => import("./Components/TvMeditation"));
const TvChores = lazy(() => import("./Components/TvChores"));
const TvDayTrading = lazy(() => import("./Components/TvDayTrading"));
const TvWorkouts = lazy(() => import("./Components/TvWorkouts"));
const MusicPractice = lazy(() => import("./Components/MusicPractice"));
const Transcribe = lazy(() => import("./Components/Transcribe"));
const Budget = lazy(() => import("./Components/Budget"));
const Calorie = lazy(() => import("./Components/Calorie"));
const AppPlaylistRunner = lazy(() => import("./Components/AppPlaylistRunner"));
const Calendar = lazy(() => import("./Components/Calendar"));
const Routines = lazy(() => import("./Components/Routines"));
const Cs2Research = lazy(() => import("./Components/Cs2Research"));
const RoutinesWorkflowRoute = lazy(() => import("./Components/RoutinesWorkflowRoute"));
const Cs2WorkflowRoute = lazy(() => import("./Components/Cs2WorkflowRoute"));
const MealsWorkflowBuildRoute = lazy(() => import("./Components/MealsWorkflowBuild"));
const MealsWorkflowRecipes = lazy(() =>
  import("./Components/MealsWorkflowRoutes").then((m) => ({ default: m.MealsWorkflowRecipes }))
);
const MealsWorkflowPlan = lazy(() =>
  import("./Components/MealsWorkflowRoutes").then((m) => ({ default: m.MealsWorkflowPlan }))
);
const MealsWorkflowAutofillRedirect = lazy(() =>
  import("./Components/MealsWorkflowRoutes").then((m) => ({ default: m.MealsWorkflowAutofillRedirect }))
);
const MealsWorkflowShop = lazy(() =>
  import("./Components/MealsWorkflowRoutes").then((m) => ({ default: m.MealsWorkflowShop }))
);
const MealsAutomation = lazy(() => import("./Components/MealsAutomation"));
const HabitsPage = lazy(() => import("./Components/HabitsPage"));
const ChoresPage = lazy(() => import("./Components/ChoresPage"));
const ComposerPage = lazy(() => import("./Components/ComposerPage"));
const AppsPage = lazy(() => import("./Components/AppsPage"));

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const PageBox = ({ children }) => (
  <Box
    sx={{
      position: "relative",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      width: "100%",
    }}
  >
    {children}
  </Box>
);

/** Legacy bookmark — prep UI replaced by TV cook mode. */
function PrepMealRedirect() {
  return <Navigate to="/tv/cook" replace />;
}

/** Routes that use useAuth() for userId (no props needed) */
const ROUTES_WITH_USER = [
  { path: "/workflows/meals/build", Component: MealsWorkflowBuildRoute },
  { path: "/workflows/meals/recipes", Component: MealsWorkflowRecipes },
  { path: "/workflows/meals/plan/autofill", Component: MealsWorkflowAutofillRedirect },
  { path: "/workflows/meals/plan", Component: MealsWorkflowPlan },
  { path: "/workflows/meals/shop", Component: MealsWorkflowShop },
  { path: "/workflows/routines/:step", Component: RoutinesWorkflowRoute },
  { path: "/workflows/cs2/:tab", Component: Cs2WorkflowRoute },
  { path: "/meal-plans", Component: MealPlansLegacyRedirect },
  { path: "/focus", Component: Agenda },
  { path: "/prep-meal", Component: PrepMealRedirect },
  { path: "/fitness", Component: Fitness },
  { path: "/docs", Component: Docs },
  { path: "/household", Component: House },
  { path: "/lists", Component: Lists },
  { path: "/step", Component: VerticalStepperForm },
  { path: "/schedule", Component: Schedule },
  { path: "/cs2", Component: Cs2 },
  { path: "/cs2/research", Component: Cs2Research },
  { path: "/music", Component: MusicPractice },
  { path: "/transcribe", Component: Transcribe },
  { path: "/budget", Component: Budget },
  { path: "/calories", Component: Calorie },
  { path: "/recipes", Component: MealPlanForm },
  { path: "/calendar", Component: Calendar },
  { path: "/automation", Component: MealsAutomation },
  { path: "/playlists/:playlistId/run", Component: AppPlaylistRunner },
  { path: "/routines", Component: Routines },
  { path: "/habits", Component: HabitsPage },
  { path: "/chores", Component: ChoresPage },
  { path: "/composer", Component: ComposerPage },
  { path: "/apps", Component: AppsPage },
];

// -----------------------------------------------------------------------------
// Loading fallback for Suspense (lazy routes)
// -----------------------------------------------------------------------------

const RouteFallback = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 200,
    }}
  >
    <CircularProgress />
  </Box>
);

function TvRoute({ children }) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <TvShell>{children}</TvShell>
    </Suspense>
  );
}

function GuardedTvRoute({ feature, children }) {
  return (
    <TvRoute>
      <FeatureGuard feature={feature}>{children}</FeatureGuard>
    </TvRoute>
  );
}

// -----------------------------------------------------------------------------
// Auth loading fallback
// -----------------------------------------------------------------------------

const AuthLoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 200,
    }}
  >
    <CircularProgress />
  </Box>
);

// -----------------------------------------------------------------------------
// App (AuthProvider wraps so useAuth() is available in AppContent)
// -----------------------------------------------------------------------------

const AppContent = () => {
  const { user, userId, loading: authLoading } = useAuth();
  useDailyTaskReset(user);
  const [items, setItems, { registerPendingAdd }] = useUserItems(user);
  const [, setProfilePicture] = useUserProfile(userId);

  return (
    <AppLayout>
            {authLoading ? (
              <AuthLoadingFallback />
            ) : user ? (
              <>
                <OnboardingGuard>
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/sign-in" element={<Navigate to="/" replace />} />
                  <Route path="/sign-up" element={<Navigate to="/" replace />} />
                  <Route
                    path="/"
                    element={
                      <PageBox>
                        <Dashboard />
                      </PageBox>
                    }
                  />
                  <Route
                    path="/grocery/lists"
                    element={
                      <PageBox>
                        <ListsHub />
                      </PageBox>
                    }
                  />
                  <Route
                    path="/grocery"
                    element={
                      <PageBox>
                        <GroceryList items={items} setItems={setItems} registerPendingAdd={registerPendingAdd} />
                      </PageBox>
                    }
                  />
                  <Route
                    path="/invite/:token"
                    element={
                      <PageBox>
                        <InviteListJoin />
                      </PageBox>
                    }
                  />
                  <Route path="/flow" element={<Navigate to="/focus" replace />} />
                  <Route path="/agenda" element={<Navigate to="/focus" replace />} />
                  {ROUTES_WITH_USER.map(({ path, Component }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <PageBox>
                          <ComingSoonGuard path={path}>
                            {path === "/cs2" ||
                            path === "/cs2/research" ||
                            path.startsWith("/workflows/cs2") ? (
                              <FeatureGuard feature={FEATURES.CS2}>
                                <Component />
                              </FeatureGuard>
                            ) : (
                              <Component />
                            )}
                          </ComingSoonGuard>
                        </PageBox>
                      }
                    />
                  ))}
                  <Route path="/onboarding" element={<Navigate to="/?tour=1" replace />} />
                  <Route
                    path="/onboarding/automation"
                    element={<Navigate to="/?tour=1" replace />}
                  />
                  <Route
                    path="/profile"
                    element={
                      <Profile onProfilePictureUpdate={setProfilePicture} />
                    }
                  />
                  <Route
                    path="/subscription"
                    element={
                      <PageBox>
                        <Subscription />
                      </PageBox>
                    }
                  />
                  <Route
                    path="/checkout"
                    element={
                      <PageBox>
                        <CheckoutGuard>
                          <CheckoutPage />
                        </CheckoutGuard>
                      </PageBox>
                    }
                  />
                  <Route
                    path="/selling"
                    element={
                      <PageBox>
                        <ComingSoonGuard path="/selling">
                          <FeatureGuard feature={FEATURES.EBAY}>
                            <Selling />
                          </FeatureGuard>
                        </ComingSoonGuard>
                      </PageBox>
                    }
                  />
                  <Route
                    path="/ebay-callback"
                    element={
                      <PageBox>
                        <ComingSoonGuard path="/ebay-callback">
                          <FeatureGuard feature={FEATURES.EBAY}>
                            <EbayCallback />
                          </FeatureGuard>
                        </ComingSoonGuard>
                      </PageBox>
                    }
                  />
                  <Route
                    path="/search"
                    element={
                      <PageBox>
                        <ComingSoonGuard path="/search">
                          <Search />
                        </ComingSoonGuard>
                      </PageBox>
                    }
                  />
                  <Route
                    path="/custom/:cardId"
                    element={
                      <PageBox>
                        <ComingSoonGuard path="/custom">
                          <CustomCardPage />
                        </ComingSoonGuard>
                      </PageBox>
                    }
                  />
                </Routes>
                </Suspense>
                </OnboardingGuard>
              </>
            ) : (
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <PageBox>
                        <Landing />
                      </PageBox>
                    }
                  />
                  <Route
                    path="/sign-in"
                    element={
                      <PageBox>
                        <AuthForm key="signin" onAuth={() => {}} initialMode="signin" />
                      </PageBox>
                    }
                  />
                  <Route
                    path="/sign-up"
                    element={
                      <PageBox>
                        <AuthForm key="signup" onAuth={() => {}} initialMode="signup" />
                      </PageBox>
                    }
                  />
                  <Route
                    path="/invite/:token"
                    element={
                      <PageBox>
                        <InviteListJoin />
                      </PageBox>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            )}
    </AppLayout>
  );
};

const router = createBrowserRouter([
  {
    path: "/privacy",
    element: (
      <StandaloneLayout>
        <Suspense fallback={<RouteFallback />}>
          <Privacy />
        </Suspense>
      </StandaloneLayout>
    ),
  },
  {
    path: "/terms",
    element: (
      <StandaloneLayout>
        <Suspense fallback={<RouteFallback />}>
          <Terms />
        </Suspense>
      </StandaloneLayout>
    ),
  },
  {
    path: "/shared/:token",
    element: (
      <StandaloneLayout>
        <Suspense fallback={<RouteFallback />}>
          <SharedList />
        </Suspense>
      </StandaloneLayout>
    ),
  },
  {
    path: "/help",
    element: (
      <StandaloneLayout>
        <Suspense fallback={<RouteFallback />}>
          <Help />
        </Suspense>
      </StandaloneLayout>
    ),
  },
  {
    path: "/delete-account",
    element: (
      <StandaloneLayout>
        <Suspense fallback={<RouteFallback />}>
          <DeleteAccount />
        </Suspense>
      </StandaloneLayout>
    ),
  },
  {
    path: "/tv",
    element: (
      <TvRoute>
        <TvMode />
      </TvRoute>
    ),
  },
  {
    path: "/tv/cook",
    element: (
      <TvRoute>
        <TvCookMode />
      </TvRoute>
    ),
  },
  {
    path: "/tv/cs2",
    element: (
      <ComingSoonGuard path="/tv/cs2">
        <GuardedTvRoute feature={FEATURES.CS2}>
          <Cs2 tvMode />
        </GuardedTvRoute>
      </ComingSoonGuard>
    ),
  },
  {
    path: "/tv/meals",
    element: (
      <TvRoute>
        <TvMealPlan />
      </TvRoute>
    ),
  },
  {
    path: "/tv/recipes",
    element: (
      <TvRoute>
        <Navigate to="/recipes" replace state={{ tvBack: "/tv" }} />
      </TvRoute>
    ),
  },
  {
    path: "/tv/lists",
    element: (
      <TvRoute>
        <TvLists />
      </TvRoute>
    ),
  },
  {
    path: "/tv/lists/:listId",
    element: (
      <TvRoute>
        <TvLists />
      </TvRoute>
    ),
  },
  {
    path: "/tv/chores",
    element: (
      <ComingSoonGuard path="/tv/chores">
        <TvRoute>
          <TvChores />
        </TvRoute>
      </ComingSoonGuard>
    ),
  },
  {
    path: "/tv/basketball",
    element: (
      <ComingSoonGuard path="/tv/basketball">
        <GuardedTvRoute feature={FEATURES.BASKETBALL}>
          <TvBasketball />
        </GuardedTvRoute>
      </ComingSoonGuard>
    ),
  },
  {
    path: "/tv/yoga",
    element: (
      <ComingSoonGuard path="/tv/yoga">
        <GuardedTvRoute feature={FEATURES.WELLNESS}>
          <TvYoga />
        </GuardedTvRoute>
      </ComingSoonGuard>
    ),
  },
  {
    path: "/tv/stretch",
    element: (
      <ComingSoonGuard path="/tv/stretch">
        <GuardedTvRoute feature={FEATURES.WELLNESS}>
          <TvStretch />
        </GuardedTvRoute>
      </ComingSoonGuard>
    ),
  },
  {
    path: "/tv/meditation",
    element: (
      <ComingSoonGuard path="/tv/meditation">
        <GuardedTvRoute feature={FEATURES.WELLNESS}>
          <TvMeditation />
        </GuardedTvRoute>
      </ComingSoonGuard>
    ),
  },
  {
    path: "/tv/day-trading",
    element: (
      <ComingSoonGuard path="/tv/day-trading">
        <GuardedTvRoute feature={FEATURES.DAY_TRADING}>
          <TvDayTrading />
        </GuardedTvRoute>
      </ComingSoonGuard>
    ),
  },
  {
    path: "/tv/workouts",
    element: (
      <ComingSoonGuard path="/tv/workouts">
        <GuardedTvRoute feature={FEATURES.WORKOUTS}>
          <TvWorkouts />
        </GuardedTvRoute>
      </ComingSoonGuard>
    ),
  },
  { path: "*", element: <AppContent /> },
]);

const App = () => (
  <ErrorBoundary>
    <AuthProvider>
      <FeaturesProvider>
        <SubscriptionProvider>
          <RouterProvider router={router} />
        </SubscriptionProvider>
      </FeaturesProvider>
    </AuthProvider>
  </ErrorBoundary>
);

export default App;
